import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getJson, postJson, putJson, deleteJson } from "./client";

// Superset ReportSchedule (subset surfaced in the list). Create/update take the
// Athena-friendly payload below, which the backend maps to Superset's schema.
export interface SupersetAlert {
  id: number;
  name: string;
  description?: string;
  active?: boolean;
  crontab?: string;
  type?: string;

  /**
   * What this alert watches, recovered by prc-api from the condition SQL — the same parse
   * the detail endpoint runs, done per alert because Superset's report LIST API does not
   * expose `sql` at any price.
   *
   * Present so the Alerts page can filter its table by the same three controls it authors
   * with. `subject: "custom"` means a hand-written condition whose scope is unknown; such an
   * alert must never be filtered OUT, since nothing here can say what it covers. Every field
   * is optional and absent means "not narrowed on that dimension" — an alert with no
   * `facilityCode` covers both books and so matches a CAP 1 filter as well as a CAP 2 one.
   */
  subject?: "external" | "internal" | "joint" | "custom";
  facilityCode?: string;
  investor?: string;
  limitDesc?: string;
  jointId?: string;
}

export type AlertOperator = "<" | "<=" | ">" | ">=" | "==" | "!=";

export interface AlertConfigInput {
  // General
  name?: string;
  description?: string;
  owners?: number[];
  emailSubject?: string;
  active?: boolean;
  // Condition
  databaseId?: number;
  databaseName?: string;
  /**
   * Utilisation of the threshold at which the alert fires, as a percentage: 90 =
   * amber-or-worse (the default), 95 = red-only, 100 = breach only. This is what the form
   * asks for and what prc-api prefers.
   */
  alertAtPctOfThreshold?: number;
  /**
   * The same setting stated as a *distance* to the threshold — 10 meant "at 90% or worse".
   *
   * Superseded by `alertAtPctOfThreshold`, which is the complement (`100 - this`). Kept
   * because prc-api still accepts it, so an API caller written against the old field keeps
   * working; the form no longer sets it. Send one or the other, not both — the new field wins.
   */
  distToThresholdPct?: number;
  /** Which kind of thing this alert watches. Picks the backend's condition builder:
   *  the three live in three different places (risk_concentrations_trend,
   *  v_prc_concentrations_all, v_prc_joint_utilisation) and the wrong one yields an
   *  alert that silently never fires. */
  subject?: "external" | "internal" | "joint";
  /** The row from Monitor this alert watches. Omit for a whole-book alert. */
  limitId?: string;
  /** The limit's description — what the generated condition SQL actually matches on,
   *  since neither the trend table nor the view carries the synthetic limitId. */
  limitDesc?: string;
  jointId?: string;
  /**
   * CAP1 / CAP2 / INTERNAL. Narrows the alert to one book, since the same limit
   * description exists in both.
   *
   * Independent of `limitId`/`limitDesc`, which is what makes a FACILITY-LEVEL alert
   * possible: on its own it means "any limit in CAP 1". Before the Alerts page had a
   * Facility filter it was only ever set as a side effect of picking one limit, so the
   * only unnarrowed alert available was "every limit in both external books".
   */
  facilityCode?: string;
  /**
   * A registered investor code, or omitted for "every investor".
   *
   * Only bites on the INTERNAL scope. External limits carry no investor because CAP 1 and
   * CAP 2 are funded by whichever investor(s) app/utils/investors.py's
   * get_external_investors() names, so prc-api REJECTS an external alert scoped to a
   * non-funding investor rather than saving one that can never fire.
   */
  investor?: string;
  /** raw override; if omitted the backend builds the PRC breach SQL */
  sql?: string;
  validatorType?: "operator" | "not null";
  operator?: AlertOperator;
  thresholdValue?: number;
  // Contents
  contentType?: "Dashboard" | "Chart";
  dashboardId?: number;
  chartId?: number;
  // "TEXT" only. Alert email is delivered by the notification service, which sends
  // text/plain and cannot carry an attachment, so PNG/CSV are not deliverable — and the
  // Superset image no longer contains the headless browser a PNG needs. prc-api coerces
  // PNG to TEXT server-side.
  reportFormat?: "TEXT";
  // Both only affect screenshot capture, so they no longer change anything under TEXT.
  // Kept so existing saved alerts round-trip unchanged.
  customWidth?: number;
  ignoreCache?: boolean;
  // Schedule
  crontab?: string;
  timezone?: string;
  logRetention?: number;
  gracePeriod?: number;
  workingTimeout?: number;
  // Notification
  notificationMethod?: "Email" | "Slack";
  recipients?: { type: "Email" | "Slack"; value: string }[];
  ccRecipients?: string;
  bccRecipients?: string;
  slackChannel?: string;
}

export interface AlertsMeta {
  databases: { id: number; name: string }[];
  dashboards: { id: number; title: string }[];
  charts: { id: number; name: string }[];
  owners: { id: number; name: string }[];
  defaultOwnerIds: number[];
  defaultDatabaseName: string;
}

const P = "portfolio-risk-concentrations/alerts-config";

export function useAlertsConfig() {
  return useQuery({
    queryKey: ["prc-alerts-config"],
    queryFn: () => getJson<SupersetAlert[]>(P),
  });
}

/**
 * One alert, in the same shape create and update accept — so the Alerts form can be
 * prefilled from it and the result sent straight back.
 *
 * The LIST endpoint carries five fields (id, name, description, active, crontab); the
 * detail endpoint maps Superset's whole record back through the inverse of the payload
 * builder. That inverse cannot always succeed: an alert whose condition SQL was
 * hand-edited comes back with `sql` set and no structured condition, because the backend
 * verifies its own parse by regenerating the SQL and comparing. When `sql` is present the
 * form must be edited under Advanced — the raw override wins over every structured field
 * on save, so offering the simple controls would let someone change a number that then
 * did nothing.
 */
export type AlertConfigDetail = AlertConfigInput & { id: number; raw?: unknown };

export function useAlert(id: number | null) {
  return useQuery({
    queryKey: ["prc-alert", id],
    queryFn: () => getJson<AlertConfigDetail>(`${P}/${id}`),
    // Only when a row is actually being edited. `enabled` rather than a conditional hook
    // so the query cache still holds the previous alert while a second one is opened.
    enabled: id !== null,
  });
}

export function useAlertsMeta() {
  return useQuery({
    queryKey: ["prc-alerts-meta"],
    queryFn: () => getJson<AlertsMeta>(`${P}/meta`),
  });
}

/**
 * Addresses an alert is allowed to reach — the product's active subscribers.
 *
 * Served by the gateway, not prc-api: entitlement lives in MySQL and prc-api has no
 * identity layer. It is the same set the gateway enforces on save, so the picker and
 * the enforcement cannot drift. Before this existed the Alerts page took free text and
 * the gateway silently dropped anything unsubscribed.
 */
export function useAlertRecipients() {
  return useQuery({
    queryKey: ["prc-alert-recipients"],
    queryFn: () =>
      getJson<{ recipients: string[] }>(
        "portfolio-risk-concentrations/alert-recipients",
      ).then((r) => r.recipients ?? []),
  });
}

export function useCreateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AlertConfigInput) => postJson(P, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prc-alerts-config"] }),
  });
}

export function useUpdateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AlertConfigInput }) => putJson(`${P}/${id}`, body),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: ["prc-alerts-config"] });
      // Without this, reopening the alert you just edited shows the pre-edit values —
      // the list refreshes but the cached detail does not.
      qc.invalidateQueries({ queryKey: ["prc-alert", id] });
    },
  });
}

/**
 * Enable/disable one alert without touching anything else.
 *
 * Separate from useUpdateAlert because the update endpoint REBUILDS the whole Superset
 * report from the body it is given. The pause switch used to call it with a four-field
 * body, which rewrote a per-limit alert's condition into the default whole-book one and
 * blanked its recipients — silently. This hits a dedicated endpoint that sends only
 * `active`.
 */
export function useSetAlertActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      putJson(`${P}/${id}/active`, { active }),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: ["prc-alerts-config"] });
      qc.invalidateQueries({ queryKey: ["prc-alert", id] });
    },
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJson(`${P}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prc-alerts-config"] }),
  });
}
